import React, { useState, useEffect, useRef } from "react";
import RfqService from "../../Services/rfq_service";
import { useNavigate } from "react-router-dom";
import ViewOrderDetails from "./ViewOrderDetails";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null); // Filter for data
  const [selectedCard, setSelectedCard] = useState(null); // Flag for visual selection
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10; // Display 10 orders per page
  const viewOrderRef = useRef(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await RfqService.getAllSalesOrders();
        if (response.success) {
          // Sort orders by orderDate in descending order (most recent first)
          const sortedOrders = (response.data || []).sort((a, b) => {
            const dateA = a.orderDate ? new Date(a.orderDate) : new Date(0);
            const dateB = b.orderDate ? new Date(b.orderDate) : new Date(0);
            return dateB - dateA;
          });
          setOrders(sortedOrders);
          setFilteredOrders(sortedOrders); // Initially show all orders
        } else {
          setError(response.message || "Failed to fetch sales orders");
        }
      } catch (err) {
        console.error("Error fetching sales orders:", err);
        setError(
          err.message || "Something went wrong while fetching sales orders"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const handleView = (order) => {
    setSelectedOrder(order);
    // Scroll to ViewOrderDetails after a slight delay to ensure it renders
    setTimeout(() => {
      if (viewOrderRef.current) {
        viewOrderRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const closeView = () => {
    setSelectedOrder(null);
  };

  // Filter orders based on selected card
  useEffect(() => {
    if (selectedFilter === "Accepted Orders") {
      setFilteredOrders(orders.filter(order => order.salesOrderStatus === "Created"));
    } else if (selectedFilter === "Total Orders") {
      setFilteredOrders(orders); // Show all orders
    } else if (selectedFilter === "Pending Orders") {
      setFilteredOrders(orders.filter(order => order.salesOrderStatus === "Waiting For RM"));
    } else if (selectedFilter === "Active Orders") {
      setFilteredOrders(orders.filter(order =>
        ["In Synthesis", "Ready To Pack", "Packing"].includes(order.salesOrderStatus)
      ));
    } else {
      setFilteredOrders(orders); // Default to all orders if no filter
    }
    setCurrentPage(1); // Reset to first page when filter changes
  }, [selectedFilter, orders]);

  // Pagination logic
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Limit displayed page buttons to 5
  const maxPageButtons = 5;
  const halfMax = Math.floor(maxPageButtons / 2);
  let startPage = Math.max(1, currentPage - halfMax);
  let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
  if (endPage - startPage + 1 < maxPageButtons && totalPages >= maxPageButtons) {
    startPage = Math.max(1, endPage - maxPageButtons + 1);
  }
  const pageNumbers = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  );

  // Calculate dynamic values for DashboardCard
  const acceptedOrders = orders.filter(
    (order) => order.salesOrderStatus === "Created"
  ).length;
  const totalOrders = orders.length;
  const pendingOrders = orders.filter(
    (order) => order.salesOrderStatus === "Waiting For RM"
  ).length;
  const activeOrders = orders.filter((order) =>
    ["In Synthesis", "Ready To Pack", "Packing"].includes(order.salesOrderStatus)
  ).length;

  // Format date with error handling
  const formatDate = (dateString) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) {
      return "N/A";
    }
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="text-center p-4 text-red-600">{error}</div>;

  return (
    <div className="container mx-auto p-4 bg-gray-50 min-h-screen">
      {/* Back Button */}
      <button
        className="text-[#2b6e73] border border-[#2b6e73] rounded-full w-9 h-9 flex items-center justify-center mb-4"
        onClick={() => navigate("/customerPortal/MyDashboard")}
      >
        ←
      </button>

      <h2 className="text-xl font-semibold mb-2 text-[#2b6e73]">
        Sales Orders
      </h2>
      <div className="text-sm text-gray-500 mb-4">
        Status: "Created, In Synthesis, Waiting For RM, Ready To Pack, Packing"
      </div>

      {/* Dashboard Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <DashboardCard
          title="Accepted Orders"
          value={acceptedOrders}
          subtitle={`${acceptedOrders} order${acceptedOrders !== 1 ? "s" : ""} accepted`}
          onClick={() => {
            setSelectedFilter("Accepted Orders");
            setSelectedCard("Accepted Orders");
          }}
          isSelected={selectedCard === "Accepted Orders"}
        />
        <DashboardCard
          title="Total Orders"
          value={totalOrders}
          subtitle={`${totalOrders} order${totalOrders !== 1 ? "s" : ""} total`}
          onClick={() => {
            setSelectedFilter("Total Orders");
            setSelectedCard("Total Orders");
          }}
          isSelected={selectedCard === "Total Orders"}
        />
        <DashboardCard
          title="Pending Orders"
          value={pendingOrders}
          subtitle={`${pendingOrders} order${pendingOrders !== 1 ? "s" : ""} pending`}
          onClick={() => {
            setSelectedFilter("Pending Orders");
            setSelectedCard("Pending Orders");
          }}
          isSelected={selectedCard === "Pending Orders"}
        />
        <DashboardCard
          title="Active Orders"
          value={activeOrders}
          subtitle={`${activeOrders} active order${activeOrders !== 1 ? "s" : ""}`}
          onClick={() => {
            setSelectedFilter("Active Orders");
            setSelectedCard("Active Orders");
          }}
          isSelected={selectedCard === "Active Orders"}
        />
      </div>

      {/* Sales Orders Table */}
      <table className="w-full bg-white border border-gray-200">
        <thead>
          <tr className="bg-[#2b6e73]">
            <th className="p-2 text-sm text-white text-center border-b border-[#2b6e73]">
              Order No
            </th>
            <th className="p-2 text-sm text-white text-center border-b border-[#2b6e73]">
              RFQ No
            </th>
            <th className="p-2 text-sm text-white text-center border-b border-[#2b6e73]">
              Customer Name
            </th>
            <th className="p-2 text-sm text-white text-center border-b border-[#2b6e73]">
              Assignee Name
            </th>
            <th className="p-2 text-sm text-white text-center border-b border-[#2b6e73]">
              Order Date
            </th>
            <th className="p-2 text-sm text-white text-center border-b border-[#2b6e73]">
              Status
            </th>
            <th className="p-2 text-sm text-white text-center border-b border-[#2b6e73]">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {currentOrders.length > 0 ? (
            currentOrders.map((order) => (
              <tr key={order.salesOrderNo} className="border-b border-gray-200">
                <td className="p-2 text-sm text-gray-800 text-center">
                  {order.salesOrderNo || "N/A"}
                </td>
                <td className="p-2 text-sm text-gray-800 text-center">
                  {order.rfqNo || "N/A"}
                </td>
                <td className="p-2 text-sm text-gray-800 text-center">
                  {order.customerName || "N/A"}
                </td>
                <td className="p-2 text-sm text-gray-800 text-center">
                  {order.assigneeName || "N/A"}
                </td>
                <td className="p-2 text-sm text-gray-800 text-center">
                  {formatDate(order.orderDate)}
                </td>
                <td className="p-2 text-sm text-center">
                  <span
                    className={`inline-block px-2 py-1 rounded ${
                      order.salesOrderStatus === "Created"
                        ? "bg-green-200 text-green-800"
                        : order.salesOrderStatus === "Ready To Pack"
                        ? "bg-orange-200 text-orange-800"
                        : order.salesOrderStatus === "In Synthesis"
                        ? "bg-yellow-200 text-yellow-800"
                        : order.salesOrderStatus === "Waiting For RM"
                        ? "bg-blue-200 text-blue-800"
                        : order.salesOrderStatus === "Packing"
                        ? "bg-red-200 text-red-800"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {order.salesOrderStatus || "N/A"}
                  </span>
                </td>
                <td className="p-2 text-sm text-center">
                  <span
                    className="text-[#2b6e73] cursor-pointer font-semibold"
                    onClick={() => handleView(order)}
                  >
                    👁️ View
                  </span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" className="p-2 text-center text-gray-500">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
{totalPages > 1 && (
  <div className="flex justify-center mt-4 items-center gap-2">
    {/* First Button */}
    <button
      onClick={() => paginate(1)}
      disabled={currentPage === 1}
      className={`px-3 h-8 flex items-center justify-center rounded-full ${
        currentPage === 1
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
      }`}
    >
      « First
    </button>

    {/* Prev Button */}
    <button
      onClick={prevPage}
      disabled={currentPage === 1}
      className={`w-8 h-8 flex items-center justify-center rounded-full ${
        currentPage === 1
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
      }`}
    >
      {"<"}
    </button>

    {startPage > 1 && (
      <button
        onClick={() => paginate(1)}
        className="w-8 h-8 flex items-center justify-center rounded-full border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
      >
        1
      </button>
    )}
    {startPage > 2 && <span className="text-gray-500">...</span>}
    {pageNumbers.map((page) => (
      <button
        key={page}
        onClick={() => paginate(page)}
        className={`w-8 h-8 flex items-center justify-center rounded-full ${
          currentPage === page
            ? "bg-[#2b6e73] text-white"
            : "border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white"
        } transition shadow-sm hover:scale-110`}
      >
        {page}
      </button>
    ))}
    {endPage < totalPages - 1 && <span className="text-gray-500">...</span>}
    {endPage < totalPages && (
      <button
        onClick={() => paginate(totalPages)}
        className="w-8 h-8 flex items-center justify-center rounded-full border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
      >
        {totalPages}
      </button>
    )}

    {/* Next Button */}
    <button
      onClick={nextPage}
      disabled={currentPage === totalPages}
      className={`w-8 h-8 flex items-center justify-center rounded-full ${
        currentPage === totalPages
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
      }`}
    >
      {">"}
    </button>

    {/* Last Button */}
    <button
      onClick={() => paginate(totalPages)}
      disabled={currentPage === totalPages}
      className={`px-3 h-8 flex items-center justify-center rounded-full ${
        currentPage === totalPages
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
      }`}
    >
      Last »
    </button>
  </div>
)}


      {/* Conditionally render ViewOrderDetails */}
      {selectedOrder && (
        <div ref={viewOrderRef} className="mt-4">
          <ViewOrderDetails order={selectedOrder} closeView={closeView} />
        </div>
      )}
    </div>
  );
};

// Dashboard Card Component
const DashboardCard = ({ title, value, subtitle, onClick, isSelected }) => {
  return (
    <div
      className={`bg-white border-2 p-4 rounded-lg shadow-lg transform hover:scale-105 transition relative cursor-pointer ${
        isSelected ? "border-4 border-[#2b6e73] bg-gray-100" : "border-[#2b6e73]"
      }`}
      onClick={onClick}
    >
      <p className="text-lg font-bold text-[#2b6e73] whitespace-nowrap overflow-hidden text-ellipsis">
        {value} {title}
      </p>
      <p className="text-sm text-[#2b6e73]/70 mt-1">{subtitle}</p>
    </div>
  );
};

export default Orders;